import React, { Component } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import MuiAutocomplete from '@material-ui/lab/Autocomplete';
import Chip from '@material-ui/core/Chip';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';

import { ReactComponent as PopupIcon } from '#web-components/assets/icons/popupIcon.svg';
import CloseIcon from '#web-components/components/Icons/CloseIcon';
import IconButton from '#web-components/components/IconButton';
import Typography from '#web-components/components/Typography';
import Input from '#web-components/components/FormControls/Input';
import { FormControlError } from '#web-components/types/formControls';

import styles from './Autocomplete.styles';

export interface ItemProps {
  value: string,
  label: string,
  originalValue?: unknown,
}

export interface AutocompleteProps {
  name: string,
  id?: string,
  value: ItemProps | ItemProps[];
  label?: string;
  onChange: (value: ItemProps | ItemProps[] | null) => void;
  options: ItemProps[];
  error?: FormControlError;
  placeholder?: string;
  multiple?: boolean;
  fullWidth?: boolean;
  noOptionsText?: string;
  disabled?: boolean;
  onInputChange?: (value: string | number) => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  hiddenLabel?: boolean;
  tabIndex?: number;
  description?: string;
}

export interface Props extends WithStyles<typeof styles>, AutocompleteProps {}

export default class Autocomplete extends Component<Props> {
  handleChange = (
    event: React.ChangeEvent<Record<string, unknown>>,
    value: ItemProps[] | ItemProps | null,
  ) => {
    this.props.onChange(value);
  };

  handleInputChange = (value: string | number) => {
    const { onInputChange } = this.props;
    if (onInputChange) {
      onInputChange(value);
    }
  };

  render() {
    const {
      id,
      name,
      value,
      label,
      disabled,
      placeholder,
      options,
      multiple,
      fullWidth,
      noOptionsText,
      classes,
      hiddenLabel,
      onInputChange,
      onInputFocus,
      onInputBlur,
      tabIndex,
      error,
      description,
    } = this.props;

    return (
      <MuiAutocomplete
        id={id}
        classes={{
          listbox: classes.listbox,
          popupIndicator: classes.popupIndicator,
          popupIndicatorOpen: classes.popupIndicatorOpen,
          endAdornment: classes.endAdornment,
          clearIndicator: classes.clearIndicator,
          popper: classes.popper,
          paper: classes.paper,
          inputRoot: classes.inputRoot,
          input: classes.input,
        }}
        fullWidth={fullWidth}
        multiple={multiple}
        disabled={disabled}
        options={options}
        getOptionLabel={(option) => option.label || ''}
        value={value}
        renderTags={(values: ItemProps[], getTagProps) => values.map((option: ItemProps | null, index: number) => (
          <Chip
            {...getTagProps({ index })}
            key={option?.value}
            label={option?.label}
            className={classes.chip}
            deleteIcon={<IconButton className={classes.deleteIcon} aria-label="delete"><CloseIcon /></IconButton>}
          />
        ))}
        popupIcon={<PopupIcon />}
        closeIcon={<CloseIcon />}
        onInputChange={(_, newInputValue) => {
          this.handleInputChange(newInputValue);
        }}
        renderInput={(params) => (
          <Input
            {...params}
            id={id}
            name={`${name}-input`}
            label={label}
            placeholder={placeholder}
            InputProps={params.InputProps}
              // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{ ...params.inputProps, tabIndex }}
            hideLabel={hiddenLabel}
            isLabelShrink={!!placeholder}
            disabled={disabled}
            error={error}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            description={description}
          />
        )}
        renderOption={(option) => (
          <Typography variant="bodyText">{option.label}</Typography>
        )}
        noOptionsText={noOptionsText}
        onChange={this.handleChange}
        filterOptions={onInputChange ? () => options : undefined}
        getOptionSelected={(option, currentValue) => isEqual(
          omit(option, ['originalValue']),
          omit(currentValue, ['originalValue']),
        )}
      />
    );
  }
}
